import { Drawer, Box, Typography, IconButton, Stack, Divider, Avatar } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { useSelector } from 'react-redux';
import { STAGES } from '../constants';

const ICONS = { explain: SmartToyOutlinedIcon, help: MenuBookOutlinedIcon, arts: AttachFileOutlinedIcon };
const TITLES = {
  explain: "What's happening behind the scenes",
  help: 'Help-text repository',
  arts: 'Generated artifacts',
};

export default function WizardDrawer({ open, kind, onClose }) {
  const stage = useSelector((s) => s.pricingWizard.stage);
  const artifacts = useSelector((s) => s.pricingWizard.artifacts);
  const Ico = ICONS[kind] || SmartToyOutlinedIcon;
  const stageArtifacts = artifacts.filter((a) => a.stage === stage);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 420, maxWidth: '90vw' } }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={{ px: 2.25, py: 1.75, borderBottom: 1, borderColor: 'divider' }}
      >
        <Avatar
          variant="rounded"
          sx={{ width: 26, height: 26, bgcolor: 'primary.lighter', color: 'primary.dark' }}
        >
          <Ico sx={{ fontSize: 14 }} />
        </Avatar>
        <Typography variant="subtitle2" sx={{ fontSize: 14, fontWeight: 700, flex: 1 }}>
          {TITLES[kind] || 'Drawer'}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Box sx={{ p: 2.25, overflow: 'auto', flex: 1 }}>
        {kind === 'explain' && (
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.65 }}>
            {STAGES[stage]?.explainer || 'Pick a stage to see what the platform is doing in the background.'}
          </Typography>
        )}
        {kind === 'help' && (
          <>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.25 }}>
              Central source for all inline help text used across the wizard.
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            <Stack spacing={1.5}>
              {STAGES.map((s) => (
                <Box key={s.id}>
                  <Typography variant="overline" sx={{ color: 'grey.500', letterSpacing: 0.6, fontWeight: 800 }}>
                    Stage {s.id + 1} · {s.short}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    {s.explainer}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </>
        )}
        {kind === 'arts' && (
          <>
            <Typography
              variant="overline"
              sx={{ color: 'grey.500', letterSpacing: 0.6, fontWeight: 800, display: 'block', mb: 1 }}
            >
              Stage artifact
            </Typography>
            {stageArtifacts.length === 0 ? (
              <EmptyState text="No artifact yet — proceed through the stage." />
            ) : (
              <Stack spacing={1}>
                {stageArtifacts.map((a) => (
                  <ArtifactTile key={a.name} artifact={a} />
                ))}
              </Stack>
            )}
            <Typography
              variant="overline"
              sx={{ color: 'grey.500', letterSpacing: 0.6, fontWeight: 800, display: 'block', mt: 2.5, mb: 1 }}
            >
              All artifacts so far
            </Typography>
            {artifacts.length === 0 ? (
              <EmptyState text="Files will appear here as you complete stages." />
            ) : (
              <Stack spacing={1}>
                {artifacts.map((a) => (
                  <ArtifactTile key={`all-${a.stage}-${a.name}`} artifact={a} showStage />
                ))}
              </Stack>
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
}

function ArtifactTile({ artifact, showStage }) {
  return (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="center"
      sx={{
        p: 1.25,
        bgcolor: 'background.neutral',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1.25,
      }}
    >
      <Avatar variant="rounded" sx={{ width: 32, height: 32, bgcolor: 'success.lighter', color: 'success.dark' }}>
        <DescriptionOutlinedIcon sx={{ fontSize: 16 }} />
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {artifact.name}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {showStage ? `Stage ${artifact.stage + 1} · ` : ''}
          {artifact.note}
        </Typography>
      </Box>
    </Stack>
  );
}

function EmptyState({ text }) {
  return (
    <Box
      sx={{
        textAlign: 'center',
        py: 3,
        px: 1.25,
        bgcolor: 'background.neutral',
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 1.25,
      }}
    >
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {text}
      </Typography>
    </Box>
  );
}
